import { Deferred, pipe } from '@helo/utils';
import { DataTypeConfigU } from './DataTypeConfig';
import { findDataTypeConfigBreadthFirst } from './DataTypeConfig.utils';
export const createFieldMapperConfig = (config) => ({
    kind: 'FIELD_MAPPER',
    ...config,
});
export const createInputValueMapperConfig = (config) => ({
    kind: 'INPUT_VALUE_MAPPER',
    ...config,
});
export const createObjectMapperConfig = (config) => ({
    kind: 'OBJECT_MAPPER',
    sourceType: config.sourceType,
    destinationType: config.destinationType,
    fields: config.fields.map(createFieldMapperConfig),
});
export const createInputObjectMapperConfig = (config) => ({
    kind: 'INPUT_OBJECT_MAPPER',
    sourceType: config.sourceType,
    destinationType: config.destinationType,
    fields: config.fields.map(createInputValueMapperConfig),
});
export const createDataTypeConfigFromMapper = (dataTypeConfigs) => (mapper) => {
    if (mapper.kind === 'OBJECT_MAPPER') {
        const sourceConfig = dataTypeConfigs.find(c => c.kind === 'OBJECT' && c.typeName === mapper.sourceType);
        if (sourceConfig && sourceConfig.kind !== 'OBJECT') {
            throw new Error(`Source type "${mapper.sourceType}" is not an object. It is a ${sourceConfig.kind}`);
        }
        const mappedFields = sourceConfig
            ? mapper.fields
                .map(fieldMapper => {
                try {
                    const locatedConfig = findDataTypeConfigBreadthFirst(fieldMapper.locator, dataTypeConfigs)(sourceConfig);
                    const ofType = locatedConfig.kind === 'FIELD'
                        ? locatedConfig.ofType
                        : locatedConfig;
                    return DataTypeConfigU.createFieldDataTypeConfig({
                        key: fieldMapper.key,
                        ofType,
                        args: [],
                        description: undefined,
                        isNullable: false,
                        directives: [],
                        state: pipe({ enableSort: true }, Deferred.merge(locatedConfig.kind === 'FIELD'
                            ? (locatedConfig.state ?? {})
                            : {}), Deferred.merge(fieldMapper.state ?? {})),
                        meta: {
                            path: [
                                ['typeName', mapper.destinationType],
                                ['key', fieldMapper.key],
                            ],
                            unmappedPath: locatedConfig.meta?.unmappedPath ??
                                locatedConfig.meta?.path ??
                                [],
                        },
                    });
                }
                catch (error) {
                    return null;
                }
            })
                .filter((field) => field !== null)
            : [];
        return DataTypeConfigU.createObjectDataTypeConfig({
            typeName: mapper.destinationType,
            kind: 'OBJECT',
            fields: mappedFields,
            description: undefined,
            isNullable: false,
            directives: [],
            state: pipe(sourceConfig?.state ?? {}, Deferred.merge(mapper.state ?? {})),
            meta: {
                path: [['typeName', mapper.destinationType]],
                unmappedPath: [],
            },
        });
    }
    if (mapper.kind === 'INPUT_OBJECT_MAPPER') {
        const sourceConfig = dataTypeConfigs.find(c => c.kind === 'INPUT_OBJECT' && c.typeName === mapper.sourceType);
        if (sourceConfig && sourceConfig.kind !== 'INPUT_OBJECT') {
            throw new Error(`Source type "${mapper.sourceType}" is not an input object. It is a ${sourceConfig.kind}`);
        }
        const mappedInputFields = sourceConfig
            ? mapper.fields
                .map(inputValueMapper => {
                try {
                    const locatedConfig = findDataTypeConfigBreadthFirst(inputValueMapper.locator, dataTypeConfigs)(sourceConfig);
                    const ofType = locatedConfig.kind === 'INPUT_VALUE'
                        ? locatedConfig.ofType
                        : locatedConfig;
                    return DataTypeConfigU.createInputValueDataTypeConfig({
                        key: inputValueMapper.key,
                        ofType,
                        defaultValue: undefined,
                        isNullable: false,
                        directives: [],
                        state: pipe({ enableSort: true }, Deferred.merge(locatedConfig.kind === 'INPUT_VALUE'
                            ? (locatedConfig.state ?? {})
                            : {}), Deferred.merge(inputValueMapper.state ?? {})),
                        meta: {
                            path: [
                                ['typeName', mapper.destinationType],
                                ['key', inputValueMapper.key],
                            ],
                            unmappedPath: locatedConfig.meta?.unmappedPath ??
                                locatedConfig.meta?.path ??
                                [],
                        },
                    });
                }
                catch (error) {
                    return null;
                }
            })
                .filter((field) => field !== null)
            : [];
        return DataTypeConfigU.createInputObjectDataTypeConfig({
            typeName: mapper.destinationType,
            kind: 'INPUT_OBJECT',
            fields: mappedInputFields,
            description: undefined,
            isNullable: false,
            directives: [],
            state: pipe(sourceConfig?.state ?? {}, Deferred.merge(mapper.state ?? {})),
            meta: {
                path: [['typeName', mapper.destinationType]],
                unmappedPath: [],
            },
        });
    }
    throw new Error(`Unknown mapper kind: ${mapper.kind}`);
};
export const mapDataTypeConfig = (dataTypeConfigs, mappers) => (config) => {
    const mapFn = mapDataTypeConfig(dataTypeConfigs, mappers);
    if (config.kind === 'OBJECT') {
        const mapper = mappers.find(m => m.sourceType === config.typeName);
        if (!mapper) {
            return {
                ...config,
                fields: config.fields.map(mapFn),
            };
        }
        if (mapper.kind !== 'OBJECT_MAPPER') {
            throw new Error(`Mapper for OBJECT "${config.typeName}" must be an OBJECT_MAPPER, but found ${mapper.kind}`);
        }
        const destinationConfig = dataTypeConfigs.find(c => c.kind === 'OBJECT' && c.typeName === mapper.destinationType);
        if (!destinationConfig) {
            throw new Error(`Destination type "${mapper.destinationType}" not found in data type configs`);
        }
        if (destinationConfig.kind !== 'OBJECT') {
            throw new Error(`Destination type "${mapper.destinationType}" is not an object. It is a ${destinationConfig.kind}`);
        }
        const mappedFields = mapper.fields
            .map(fieldMapper => {
            const destField = destinationConfig.fields.find(f => f.key === fieldMapper.key);
            if (!destField) {
                return null;
            }
            try {
                const locatedConfig = findDataTypeConfigBreadthFirst(fieldMapper.locator, dataTypeConfigs)(config);
                const ofType = locatedConfig.kind === 'FIELD'
                    ? mapFn(locatedConfig.ofType)
                    : locatedConfig;
                return DataTypeConfigU.createFieldDataTypeConfig({
                    key: fieldMapper.key,
                    ofType,
                    args: [],
                    description: undefined,
                    isNullable: false,
                    directives: [],
                    state: pipe({ enableSort: true }, Deferred.merge(locatedConfig.kind === 'FIELD'
                        ? (locatedConfig.state ?? {})
                        : {}), Deferred.merge(destField.state ?? {}), Deferred.merge(fieldMapper.state ?? {})),
                    meta: {
                        path: [
                            ['typeName', mapper.destinationType],
                            ['key', fieldMapper.key],
                        ],
                        unmappedPath: locatedConfig.meta?.unmappedPath ??
                            locatedConfig.meta?.path ??
                            [],
                    },
                });
            }
            catch (error) {
                return null;
            }
        })
            .filter((field) => field !== null);
        return DataTypeConfigU.createObjectDataTypeConfig({
            typeName: mapper.destinationType,
            kind: 'OBJECT',
            fields: mappedFields,
            description: destinationConfig.description,
            isNullable: destinationConfig.isNullable,
            directives: destinationConfig.directives,
            state: pipe(config.state ?? {}, Deferred.merge(destinationConfig.state ?? {}), Deferred.merge(mapper.state ?? {})),
            meta: {
                path: [['typeName', mapper.destinationType]],
                unmappedPath: config.meta?.unmappedPath ?? config.meta?.path ?? [],
            },
        });
    }
    switch (config.kind) {
        case 'FIELD':
            return {
                ...config,
                ofType: mapFn(config.ofType),
                args: config.args,
            };
        case 'LIST':
            return {
                ...config,
                ofType: mapFn(config.ofType),
            };
        case 'UNION':
            return {
                ...config,
                possibleTypes: config.possibleTypes.map(mapFn),
            };
        case 'INPUT_OBJECT': {
            const mapper = mappers.find(m => m.sourceType === config.typeName);
            if (!mapper) {
                return {
                    ...config,
                    fields: config.fields.map(mapFn),
                };
            }
            if (mapper.kind !== 'INPUT_OBJECT_MAPPER') {
                throw new Error(`Mapper for INPUT_OBJECT "${config.typeName}" must be an INPUT_OBJECT_MAPPER, but found ${mapper.kind}`);
            }
            const destinationConfig = dataTypeConfigs.find(c => c.kind === 'INPUT_OBJECT' && c.typeName === mapper.destinationType);
            if (!destinationConfig) {
                throw new Error(`Destination type "${mapper.destinationType}" not found in data type configs`);
            }
            if (destinationConfig.kind !== 'INPUT_OBJECT') {
                throw new Error(`Destination type "${mapper.destinationType}" is not an input object. It is a ${destinationConfig.kind}`);
            }
            const mappedInputFields = mapper.fields
                .map(inputValueMapper => {
                const destField = destinationConfig.fields.find(f => f.key === inputValueMapper.key);
                if (!destField) {
                    return null;
                }
                try {
                    const locatedConfig = findDataTypeConfigBreadthFirst(inputValueMapper.locator, dataTypeConfigs)(config);
                    const ofType = locatedConfig.kind === 'INPUT_VALUE'
                        ? mapFn(locatedConfig.ofType)
                        : locatedConfig;
                    return DataTypeConfigU.createInputValueDataTypeConfig({
                        key: inputValueMapper.key,
                        ofType,
                        defaultValue: undefined,
                        isNullable: false,
                        directives: [],
                        state: pipe({ enableSort: true }, Deferred.merge(locatedConfig.kind === 'INPUT_VALUE'
                            ? (locatedConfig.state ?? {})
                            : {}), Deferred.merge(destField.state ?? {}), Deferred.merge(inputValueMapper.state ?? {})),
                        meta: {
                            path: [
                                ['typeName', mapper.destinationType],
                                ['key', inputValueMapper.key],
                            ],
                            unmappedPath: locatedConfig.meta?.unmappedPath ??
                                locatedConfig.meta?.path ??
                                [],
                        },
                    });
                }
                catch (error) {
                    return null;
                }
            })
                .filter((field) => field !== null);
            return DataTypeConfigU.createInputObjectDataTypeConfig({
                typeName: mapper.destinationType,
                kind: 'INPUT_OBJECT',
                fields: mappedInputFields,
                description: destinationConfig.description,
                isNullable: destinationConfig.isNullable,
                directives: destinationConfig.directives,
                state: pipe(config.state ?? {}, Deferred.merge(destinationConfig.state ?? {}), Deferred.merge(mapper.state ?? {})),
                meta: {
                    path: [['typeName', mapper.destinationType]],
                    unmappedPath: config.meta?.unmappedPath ?? config.meta?.path ?? [],
                },
            });
        }
        case 'INPUT_VALUE':
            return {
                ...config,
                ofType: mapFn(config.ofType),
            };
        case 'ENUM':
        case 'SCALAR':
        case 'TYPE_REFERENCE':
        case 'ENUM_VALUE':
        default:
            return config;
    }
};
export const mapDataTypeConfigLazy = (dataTypeConfigs, mappers) => (config) => {
    const mapFn = mapDataTypeConfigLazy(dataTypeConfigs, mappers);
    if (config.kind === 'OBJECT') {
        const mapper = mappers.find(m => m.sourceType === config.typeName);
        if (!mapper) {
            const lazyConfig = {
                ...config,
            };
            Deferred.setField('fields', () => config.fields.map(mapFn))(lazyConfig);
            return lazyConfig;
        }
        if (mapper.kind !== 'OBJECT_MAPPER') {
            throw new Error(`Mapper for OBJECT "${config.typeName}" must be an OBJECT_MAPPER, but found ${mapper.kind}`);
        }
        const destinationConfig = dataTypeConfigs.find(c => c.kind === 'OBJECT' && c.typeName === mapper.destinationType);
        if (!destinationConfig) {
            throw new Error(`Destination type "${mapper.destinationType}" not found in data type configs`);
        }
        if (destinationConfig.kind !== 'OBJECT') {
            throw new Error(`Destination type "${mapper.destinationType}" is not an object. It is a ${destinationConfig.kind}`);
        }
        const mappedConfig = DataTypeConfigU.createObjectDataTypeConfig({
            typeName: mapper.destinationType,
            kind: 'OBJECT',
            fields: [],
            description: destinationConfig.description,
            isNullable: destinationConfig.isNullable,
            directives: destinationConfig.directives,
            state: pipe(config.state ?? {}, Deferred.merge(destinationConfig.state ?? {}), Deferred.merge(mapper.state ?? {})),
            meta: {
                path: [['typeName', mapper.destinationType]],
                unmappedPath: config.meta?.unmappedPath ?? config.meta?.path ?? [],
            },
        });
        Deferred.setField('fields', () => {
            const mappedFields = mapper.fields
                .map(fieldMapper => {
                const destField = destinationConfig.fields.find(f => f.key === fieldMapper.key);
                if (!destField) {
                    return null;
                }
                try {
                    const locatedConfig = findDataTypeConfigBreadthFirst(fieldMapper.locator, dataTypeConfigs)(config);
                    const ofType = locatedConfig.kind === 'FIELD'
                        ? mapFn(locatedConfig.ofType)
                        : locatedConfig;
                    return DataTypeConfigU.createFieldDataTypeConfig({
                        key: fieldMapper.key,
                        ofType,
                        args: [],
                        description: undefined,
                        isNullable: false,
                        directives: [],
                        state: pipe({ enableSort: true }, Deferred.merge(locatedConfig.kind === 'FIELD'
                            ? (locatedConfig.state ?? {})
                            : {}), Deferred.merge(destField.state ?? {}), Deferred.merge(fieldMapper.state ?? {})),
                        meta: {
                            path: [
                                ['typeName', mapper.destinationType],
                                ['key', fieldMapper.key],
                            ],
                            unmappedPath: locatedConfig.meta?.unmappedPath ??
                                locatedConfig.meta?.path ??
                                [],
                        },
                    });
                }
                catch (error) {
                    return null;
                }
            })
                .filter((field) => field !== null);
            return mappedFields;
        })(mappedConfig);
        return mappedConfig;
    }
    switch (config.kind) {
        case 'FIELD': {
            const lazyConfig = {
                ...config,
            };
            Deferred.setField('ofType', () => mapFn(config.ofType))(lazyConfig);
            return lazyConfig;
        }
        case 'LIST': {
            const lazyConfig = {
                ...config,
            };
            Deferred.setField('ofType', () => mapFn(config.ofType))(lazyConfig);
            return lazyConfig;
        }
        case 'UNION': {
            const lazyConfig = {
                ...config,
            };
            Deferred.setField('possibleTypes', () => config.possibleTypes.map(mapFn))(lazyConfig);
            return lazyConfig;
        }
        case 'INPUT_OBJECT': {
            const mapper = mappers.find(m => m.sourceType === config.typeName);
            if (!mapper) {
                const lazyConfig = {
                    ...config,
                };
                Deferred.setField('fields', () => config.fields.map(mapFn))(lazyConfig);
                return lazyConfig;
            }
            if (mapper.kind !== 'INPUT_OBJECT_MAPPER') {
                throw new Error(`Mapper for INPUT_OBJECT "${config.typeName}" must be an INPUT_OBJECT_MAPPER, but found ${mapper.kind}`);
            }
            const destinationConfig = dataTypeConfigs.find(c => c.kind === 'INPUT_OBJECT' && c.typeName === mapper.destinationType);
            if (!destinationConfig) {
                throw new Error(`Destination type "${mapper.destinationType}" not found in data type configs`);
            }
            if (destinationConfig.kind !== 'INPUT_OBJECT') {
                throw new Error(`Destination type "${mapper.destinationType}" is not an input object. It is a ${destinationConfig.kind}`);
            }
            const mappedConfig = DataTypeConfigU.createInputObjectDataTypeConfig({
                typeName: mapper.destinationType,
                kind: 'INPUT_OBJECT',
                fields: [],
                description: destinationConfig.description,
                isNullable: destinationConfig.isNullable,
                directives: destinationConfig.directives,
                state: pipe(config.state ?? {}, Deferred.merge(destinationConfig.state ?? {}), Deferred.merge(mapper.state ?? {})),
                meta: {
                    path: [['typeName', mapper.destinationType]],
                    unmappedPath: config.meta?.unmappedPath ?? config.meta?.path ?? [],
                },
            });
            Deferred.setField('fields', () => {
                const mappedInputFields = mapper.fields
                    .map(inputValueMapper => {
                    const destField = destinationConfig.fields.find(f => f.key === inputValueMapper.key);
                    if (!destField) {
                        return null;
                    }
                    try {
                        const locatedConfig = findDataTypeConfigBreadthFirst(inputValueMapper.locator, dataTypeConfigs)(config);
                        const ofType = locatedConfig.kind === 'INPUT_VALUE'
                            ? mapFn(locatedConfig.ofType)
                            : locatedConfig;
                        return DataTypeConfigU.createInputValueDataTypeConfig({
                            key: inputValueMapper.key,
                            ofType,
                            defaultValue: undefined,
                            isNullable: false,
                            directives: [],
                            state: pipe({ enableSort: true }, Deferred.merge(locatedConfig.kind === 'INPUT_VALUE'
                                ? (locatedConfig.state ?? {})
                                : {}), Deferred.merge(destField.state ?? {}), Deferred.merge(inputValueMapper.state ?? {})),
                            meta: {
                                path: [
                                    ['typeName', mapper.destinationType],
                                    ['key', inputValueMapper.key],
                                ],
                                unmappedPath: locatedConfig.meta?.unmappedPath ??
                                    locatedConfig.meta?.path ??
                                    [],
                            },
                        });
                    }
                    catch (error) {
                        return null;
                    }
                })
                    .filter((field) => field !== null);
                return mappedInputFields;
            })(mappedConfig);
            return mappedConfig;
        }
        case 'INPUT_VALUE': {
            const lazyConfig = {
                ...config,
            };
            Deferred.setField('ofType', () => mapFn(config.ofType))(lazyConfig);
            return lazyConfig;
        }
        case 'ENUM':
        case 'SCALAR':
        case 'TYPE_REFERENCE':
        case 'ENUM_VALUE':
        default:
            return config;
    }
};
//# sourceMappingURL=DataTypeConfig-mapper.js.map